import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { salaryAPI, employeeAPI, companyAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { DataTable, SortableHeader, DataTableViewOptions, useDataTable } from '../../components/ui/data-table';
import { Checkbox } from '../../components/ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Loader2, Edit, Download, Trash2, MoreHorizontal } from 'lucide-react';
import { formatIndianCurrency } from '../../lib/utils';
import { calculatePFEstimate } from '../../lib/payrollUtils';
import * as XLSX from 'xlsx';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import ModuleDisabled from '../../components/shared/ModuleDisabled';

const SALARY_EXPORT_FIELDS = [
    { id: 'employee_name', label: 'Employee Name', category: 'Basic' },
    { id: 'ctc', label: 'Annual CTC', category: 'Basic' },
    { id: 'effective_from', label: 'Effective From', category: 'Basic' },
    { id: 'basic', label: 'Basic Pay', category: 'Components' },
    { id: 'hra', label: 'HRA', category: 'Components' },
    { id: 'special_allowance', label: 'Special Allowance', category: 'Components' },
    { id: 'travel_allowance', label: 'Travel Allowance', category: 'Components' },
    { id: 'lta', label: 'LTA', category: 'Components' },
    { id: 'uniform_allowance', label: 'Uniform Allowance', category: 'Components' },
    { id: 'children_education_allowance', label: 'Children Education', category: 'Components' },
    { id: 'statutory_bonus', label: 'Statutory Allowance', category: 'Components' },
    { id: 'pf_employer', label: 'PF Employer', category: 'Statutory' },
    { id: 'esi_employer', label: 'ESI Employer', category: 'Statutory' },
    { id: 'monthly_tds', label: 'Monthly TDS', category: 'Statutory' },
];

const calculateSalaryComponents = (ctc, pfConfig = null) => {
    const annualCTC = Number(ctc) || 0;
    if (annualCTC === 0) return null;

    const basic = Math.round(annualCTC * 0.5);
    const hra = Math.round(basic * 0.4);

    const monthlyBasic = basic / 12;
    const pfEstimate = pfConfig ? calculatePFEstimate(monthlyBasic, pfConfig).employerPF : Math.min(Math.round(monthlyBasic * 0.12), 1800);
    const pfEmployerMonthly = Math.round(pfEstimate);
    const pfEmployerAnnual = pfEmployerMonthly * 12;

    const monthlyGross = annualCTC / 12;
    const esiEmployerMonthly = monthlyGross <= 21000 ? Math.round(monthlyGross * 0.0325) : 0;
    const esiEmployerAnnual = esiEmployerMonthly * 12;

    const children = 14400;
    const lta = Math.round(annualCTC * 0.03);
    const statutory = Math.round(annualCTC * 0.03);
    const travel = 19200;
    const uniform = 30000;

    const sumOther = basic + hra + esiEmployerAnnual + children + lta + statutory + travel + uniform;
    const special = Math.round(annualCTC - sumOther);

    return {
        basic,
        hra,
        pf_employer: pfEmployerAnnual,
        esi_employer: esiEmployerAnnual,
        children_education_allowance: children,
        lta,
        statutory_bonus: statutory,
        travel_allowance: travel,
        uniform_allowance: uniform,
        special_allowance: Math.max(0, special),
    };
};

const SalaryStructure = () => {
    const { user } = useAuth();
    const [structures, setStructures] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [selectedStructure, setSelectedStructure] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [selectedExportFields, setSelectedExportFields] = useState(SALARY_EXPORT_FIELDS.filter(f => f.category === 'Basic').map(f => f.id));
    const [createLoading, setCreateLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [pfConfig, setPfConfig] = useState({ type: 'percentage', value: 12 });
    const { rowSelection, setRowSelection, columnVisibility, setColumnVisibility } = useDataTable();
    const [formData, setFormData] = useState({
        employee_id: '',
        ctc: '',
        basic: '',
        hra: '',
        pf_employer: '',
        special_allowance: '',
        travel_allowance: '',
        uniform_allowance: '',
        lta: '',
        children_education_allowance: '',
        statutory_bonus: '',
        esi_employer: '',
        effective_from: new Date().toISOString().split('T')[0],
    });

    const handleCTCChange = (value) => {
        const ctc = parseFloat(value) || 0;
        if (ctc === 0) {
            setFormData(prev => ({ ...prev, ctc: value }));
            return;
        }

        const components = calculateSalaryComponents(ctc, pfConfig);
        setFormData(prev => ({
            ...prev,
            ctc: value,
            basic: components.basic.toString(),
            hra: components.hra.toString(),
            pf_employer: components.pf_employer.toString(),
            esi_employer: components.esi_employer.toString(),
            children_education_allowance: components.children_education_allowance.toString(),
            lta: components.lta.toString(),
            statutory_bonus: components.statutory_bonus.toString(),
            travel_allowance: components.travel_allowance.toString(),
            uniform_allowance: components.uniform_allowance.toString(),
            special_allowance: components.special_allowance.toString(),
        }));
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [structuresRes, employeesRes, companyRes] = await Promise.all([
                salaryAPI.getStructures({ page: 1, page_size: 100 }),
                employeeAPI.getEmployees({ page: 1, page_size: 1000, status: 'ACTIVE' }),
                companyAPI.getCurrent(),
            ]);
            setStructures(structuresRes.data);
            setEmployees(employeesRes.data.employees);
            if (companyRes.data?.pf_contribution) setPfConfig(companyRes.data.pf_contribution);
        } catch (error) {
            console.error('Fetch error:', error);
            if (error.response?.status === 403) {
                setError(error.response.data.detail);
            } else {
                toast.error('Failed to fetch data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCreateStructure = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        try {
            const payload = { ...formData };
            Object.keys(payload).forEach(key => {
                if (payload[key] === '' && key !== 'employee_id' && key !== 'effective_from') {
                    payload[key] = 0;
                }
            });

            if (isEdit) {
                await salaryAPI.updateStructure(editingId, {
                    ...payload,
                    company_id: user.company_id,
                });
                toast.success('Salary structure updated');
            } else {
                await salaryAPI.createStructure({
                    ...payload,
                    company_id: user.company_id,
                    components: [],
                });
                toast.success('Salary structure created');
            }
            setIsCreateOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save salary structure');
        } finally {
            setCreateLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            employee_id: '',
            ctc: '',
            basic: '',
            hra: '',
            pf_employer: '',
            special_allowance: '',
            travel_allowance: '',
            uniform_allowance: '',
            lta: '',
            children_education_allowance: '',
            statutory_bonus: '',
            esi_employer: '',
            effective_from: new Date().toISOString().split('T')[0],
        });
        setIsEdit(false);
        setEditingId(null);
    };

    const handleEdit = (structure) => {
        // Avoid injecting the old fixed 21,000 defaults for LTA / statutory bonus.
        // If the stored value is missing, leave the field blank; the backend will
        // fall back to the current 3%-of-CTC formula when payroll is processed.
        setFormData({
            employee_id: structure.employee_id,
            ctc: structure.ctc?.toString() || '',
            basic: structure.basic?.toString() || '',
            hra: structure.hra?.toString() || '',
            pf_employer: structure.pf_employer?.toString() || '',
            special_allowance: structure.special_allowance?.toString() || '',
            travel_allowance: structure.travel_allowance?.toString() || '',
            uniform_allowance: structure.uniform_allowance?.toString() || '',
            lta: structure.lta?.toString() || '',
            children_education_allowance: structure.children_education_allowance?.toString() || '',
            statutory_bonus: structure.statutory_bonus?.toString() || '',
            esi_employer: structure.esi_employer?.toString() || '',
            effective_from: structure.effective_from || new Date().toISOString().split('T')[0],
        });
        setIsEdit(true);
        setEditingId(structure.id);
        setIsCreateOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this salary structure?')) return;
        try {
            await salaryAPI.deleteStructure(id);
            toast.success('Salary structure deleted');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete salary structure');
        }
    };

    const handleDownloadExcel = () => {
        const selectedIds = Object.keys(rowSelection);
        if (selectedIds.length === 0) {
            toast.error('Select records to export');
            return;
        }
        setIsExportOpen(true);
    };

    const performExport = () => {
        const selectedData = structures.filter((_, index) => rowSelection[index]);

        if (selectedData.length === 0) {
            toast.error('No data found for selection');
            return;
        }

        if (selectedExportFields.length === 0) {
            toast.error('Select at least one field to export');
            return;
        }

        const dataToExport = selectedData.map(item => {
            const row = {};
            selectedExportFields.forEach(fieldId => {
                const field = SALARY_EXPORT_FIELDS.find(f => f.id === fieldId);
                let value = item[fieldId];

                if (['ctc', 'basic', 'hra', 'special_allowance', 'travel_allowance', 'lta', 'uniform_allowance', 'children_education_allowance', 'statutory_bonus', 'pf_employer', 'esi_employer', 'monthly_tds'].includes(fieldId)) {
                    value = value || 0;
                }

                row[field.label] = value || '-';
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Salary Structures");

        // Auto-size columns
        const maxWidths = dataToExport.reduce((acc, row) => {
            Object.keys(row).forEach((key, i) => {
                const val = String(row[key]);
                acc[i] = Math.max(acc[i] || 0, val.length, key.length);
            });
            return acc;
        }, []);
        worksheet['!cols'] = maxWidths.map(w => ({ wch: w + 2 }));

        XLSX.writeFile(workbook, `salary_structures_${new Date().toISOString().split('T')[0]}.xlsx`);
        setIsExportOpen(false);
        toast.success('Excel downloaded successfully');
    };

    const columns = [
        {
            id: 'select',
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                    className="translate-y-[2px] ml-1"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                    className="translate-y-[2px] ml-1"
                    onClick={(e) => e.stopPropagation()}
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: 'employee_name',
            header: ({ column }) => <SortableHeader column={column} title="Employee" />,
            cell: ({ row }) => (
                <Button
                    variant="link"
                    className="p-0 h-auto font-bold text-indigo-600 hover:text-indigo-800"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedStructure(row.original);
                        setIsDetailsOpen(true);
                    }}
                >
                    {row.original.employee_name}
                </Button>
            ),
        },
        {
            accessorKey: 'ctc',
            header: ({ column }) => <SortableHeader column={column} title="CTC (Annual)" />,
            cell: ({ row }) => formatIndianCurrency(row.original.ctc),
        },
        {
            accessorKey: 'basic',
            header: ({ column }) => <SortableHeader column={column} title="Basic (Annual)" />,
            cell: ({ row }) => formatIndianCurrency(row.original.basic),
        },
        {
            accessorKey: 'hra',
            header: ({ column }) => <SortableHeader column={column} title="HRA (Annual)" />,
            cell: ({ row }) => formatIndianCurrency(row.original.hra),
        },
        {
            accessorKey: 'special_allowance',
            header: ({ column }) => <SortableHeader column={column} title="Special (Annual)" />,
            cell: ({ row }) => formatIndianCurrency(row.original.special_allowance),
        },
        {
            accessorKey: 'monthly_tds',
            header: ({ column }) => <SortableHeader column={column} title="TDS (Monthly)" />,
            cell: ({ row }) => (
                <span className="text-red-600 font-semibold">
                    {formatIndianCurrency(Math.round(row.original.monthly_tds || 0))}
                </span>
            ),
        },
        {
            accessorKey: 'effective_from',
            header: ({ column }) => <SortableHeader column={column} title="Effective From" />,
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-blue-600"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(row.original);
                        }}
                    >
                        <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(row.original.id);
                        }}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    if (error) {
        return <ModuleDisabled moduleName="salary_structure" />;
    }

    return (
        <div className="space-y-6 animate-slide-up">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Salary Structure</h1>
                    <p className="text-muted-foreground">Manage employee salary structures</p>
                </div>
                <Dialog open={isCreateOpen} onOpenChange={(open) => {
                    setIsCreateOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button data-testid="create-salary-btn" onClick={() => setIsEdit(false)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add Salary Structure
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{isEdit ? 'Edit Salary Structure' : 'Create Salary Structure'}</DialogTitle>
                            <DialogDescription>
                                {isEdit ? 'Update compensation structure for an employee' : 'Define compensation structure for an employee'}
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateStructure}>
                            <div className="grid gap-4 py-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Employee *</Label>
                                        <Select
                                            value={formData.employee_id}
                                            onValueChange={(v) => setFormData({ ...formData, employee_id: v })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select employee" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {employees.map((emp) => (
                                                    <SelectItem key={emp.id} value={emp.id}>
                                                        {emp.first_name} {emp.last_name} ({emp.employee_code})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Effective From *</Label>
                                        <Input
                                            type="date"
                                            value={formData.effective_from}
                                            onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Annual CTC *</Label>
                                    <Input
                                        type="number"
                                        value={formData.ctc}
                                        onChange={(e) => handleCTCChange(e.target.value)}
                                        placeholder="Enter annual CTC"
                                        required
                                    />
                                </div>

                                <div className="border-t pt-4 mt-2">
                                    <h4 className="font-medium mb-3">Annual Components</h4>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Basic</Label>
                                            <Input
                                                type="number"
                                                value={formData.basic}
                                                onChange={(e) => setFormData({ ...formData, basic: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>HRA</Label>
                                            <Input
                                                type="number"
                                                value={formData.hra}
                                                onChange={(e) => setFormData({ ...formData, hra: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Special Allowance</Label>
                                            <Input
                                                type="number"
                                                value={formData.special_allowance}
                                                onChange={(e) => setFormData({ ...formData, special_allowance: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Travel Allowance</Label>
                                            <Input
                                                type="number"
                                                value={formData.travel_allowance}
                                                onChange={(e) => setFormData({ ...formData, travel_allowance: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>LTA</Label>
                                            <Input
                                                type="number"
                                                value={formData.lta}
                                                onChange={(e) => setFormData({ ...formData, lta: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Uniform Allowance</Label>
                                            <Input
                                                type="number"
                                                value={formData.uniform_allowance}
                                                onChange={(e) => setFormData({ ...formData, uniform_allowance: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Children Education</Label>
                                            <Input
                                                type="number"
                                                value={formData.children_education_allowance}
                                                onChange={(e) => setFormData({ ...formData, children_education_allowance: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Statutory Allowance</Label>
                                            <Input
                                                type="number"
                                                value={formData.statutory_bonus}
                                                onChange={(e) => setFormData({ ...formData, statutory_bonus: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <DialogFooter className="mt-6">
                                <Button type="button" variant="outline" onClick={() => {
                                    setIsCreateOpen(false);
                                    resetForm();
                                }}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={createLoading}>
                                    {createLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                    {isEdit ? 'Update' : 'Create'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {Object.keys(rowSelection).length > 0 && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center justify-between mb-2 animate-in fade-in slide-in-from-top-2">
                    <p className="text-sm font-medium text-primary">
                        {Object.keys(rowSelection).length} structures selected
                    </p>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-primary/20 hover:bg-primary/10 hover:text-primary flex items-center gap-2"
                            onClick={handleDownloadExcel}
                        >
                            <Download className="h-3.5 w-3.5" />
                            Download Excel
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setRowSelection({})}>
                            Clear
                        </Button>
                    </div>
                </div>
            )}

            <DataTable
                columns={columns}
                data={structures}
                loading={loading}
                emptyMessage="No salary structures found."
                onRowClick={(row) => {
                    setSelectedStructure(row);
                    setIsDetailsOpen(true);
                }}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
            />
            <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold text-indigo-700">Salary Structure Details</DialogTitle>
                        <DialogDescription>
                            Detailed view of compensation components and tax projections
                        </DialogDescription>
                    </DialogHeader>

                    {selectedStructure && (
                        <div className="grid gap-6">
                            <div className="flex justify-between items-start pb-4 border-b">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900">{selectedStructure.employee_name}</h3>
                                    <p className="text-sm text-muted-foreground">Effective From: {selectedStructure.effective_from}</p>
                                </div>
                                <div className="text-right">
                                    <Badge variant="outline" className="px-4 py-1 text-lg font-bold border-indigo-200 text-indigo-700 bg-indigo-50">
                                        CTC: {formatIndianCurrency(selectedStructure.ctc)}
                                    </Badge>
                                    <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-widest">Annual Cost to Company</p>
                                </div>
                            </div>

                            <div className="grid gap-8 md:grid-cols-2">
                                {/* Left Column: Earnings */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-xs font-bold tracking-wider uppercase text-slate-500">Monthly Earnings</h4>
                                        <span className="text-xs font-medium text-slate-400">Additions</span>
                                    </div>
                                    <div className="space-y-1 rounded-xl border border-slate-100 p-4 bg-white shadow-sm">
                                        <div className="flex justify-between border-b border-slate-50 py-3 text-sm">
                                            <span className="text-slate-600">Basic Pay</span>
                                            <span className="font-bold text-slate-900">{formatIndianCurrency(selectedStructure.basic / 12)}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-50 py-3 text-sm">
                                            <span className="text-slate-600">HRA</span>
                                            <span className="font-bold text-slate-900">{formatIndianCurrency(selectedStructure.hra / 12)}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-50 py-3 text-sm">
                                            <span className="text-slate-600">Special Allowance</span>
                                            <span className="font-bold text-slate-900">{formatIndianCurrency(selectedStructure.special_allowance / 12)}</span>
                                        </div>
                                        {selectedStructure.travel_allowance > 0 && (
                                            <div className="flex justify-between border-b border-slate-50 py-3 text-sm">
                                                <span className="text-slate-600">Travel Allowance</span>
                                                <span className="font-bold text-slate-900">{formatIndianCurrency(selectedStructure.travel_allowance / 12)}</span>
                                            </div>
                                        )}
                                        {selectedStructure.lta > 0 && (
                                            <div className="flex justify-between border-b border-slate-50 py-3 text-sm">
                                                <span className="text-slate-600">LTA</span>
                                                <span className="font-bold text-slate-900">{formatIndianCurrency(selectedStructure.lta / 12)}</span>
                                            </div>
                                        )}
                                        {selectedStructure.uniform_allowance > 0 && (
                                            <div className="flex justify-between border-b border-slate-50 py-3 text-sm">
                                                <span className="text-slate-600">Uniform Allowance</span>
                                                <span className="font-bold text-slate-900">{formatIndianCurrency(selectedStructure.uniform_allowance / 12)}</span>
                                            </div>
                                        )}
                                        {selectedStructure.children_education_allowance > 0 && (
                                            <div className="flex justify-between border-b border-slate-50 py-3 text-sm">
                                                <span className="text-slate-600">Children Education</span>
                                                <span className="font-bold text-slate-900">{formatIndianCurrency(selectedStructure.children_education_allowance / 12)}</span>
                                            </div>
                                        )}
                                        {selectedStructure.statutory_bonus > 0 && (
                                            <div className="flex justify-between border-b border-slate-50 py-3 text-sm">
                                                <span className="text-slate-600">Statutory Allowance</span>
                                                <span className="font-bold text-slate-900">{formatIndianCurrency(selectedStructure.statutory_bonus / 12)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between pt-3 text-sm font-black text-slate-900">
                                            <span>Gross Monthly</span>
                                            <span>{formatIndianCurrency(
                                                (selectedStructure.basic || 0) / 12 +
                                                (selectedStructure.hra || 0) / 12 +
                                                (selectedStructure.special_allowance || 0) / 12 +
                                                (selectedStructure.travel_allowance || 0) / 12 +
                                                (selectedStructure.lta || 0) / 12 +
                                                (selectedStructure.uniform_allowance || 0) / 12 +
                                                (selectedStructure.children_education_allowance || 0) / 12 +
                                                (selectedStructure.statutory_bonus || 0) / 12
                                            )}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column: Deductions & Tax */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-xs font-bold tracking-wider uppercase text-slate-500">Statutory Deductions & Tax</h4>
                                        <Badge variant="secondary" className="text-[10px] font-bold uppercase">{selectedStructure.tax_regime} Regime</Badge>
                                    </div>

                                    <div className="space-y-1 rounded-xl border border-red-50 p-4 bg-red-50/30">
                                        <div className="flex justify-between border-b border-red-100 py-3 text-sm">
                                            <span className="text-slate-600">PF (Employee)</span>
                                            <span className="font-bold text-red-600">
                                                {formatIndianCurrency(calculatePFEstimate(selectedStructure.basic / 12, pfConfig).employeePF)}
                                            </span>
                                        </div>
                                        {(selectedStructure.ctc / 12) <= 21000 && (
                                            <div className="flex justify-between border-b border-red-100 py-3 text-sm">
                                                <span className="text-slate-600">ESI (Employee)</span>
                                                <span className="font-bold text-red-600">
                                                    {formatIndianCurrency(Math.round((selectedStructure.ctc / 12) * 0.0075))}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex justify-between border-b border-red-100 py-3 text-sm">
                                            <span className="text-slate-600">Professional Tax</span>
                                            <span className="font-bold text-red-600">{formatIndianCurrency(200)}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-red-100 py-3 text-sm">
                                            <div className="flex flex-col">
                                                <span className="text-slate-600">Income Tax (TDS)</span>
                                                <span className="text-[10px] text-muted-foreground italic">Based on {selectedStructure.tax_regime} Regime</span>
                                            </div>
                                            <span className="font-bold text-red-600">
                                                {formatIndianCurrency(Math.round(selectedStructure.monthly_tds || 0))}
                                            </span>
                                        </div>
                                        <div className="flex justify-between pt-3 text-sm font-black text-red-700">
                                            <span>Total Deductions</span>
                                            <span>
                                                {formatIndianCurrency(
                                                    calculatePFEstimate(selectedStructure.basic / 12, pfConfig).employeePF +
                                                    ((selectedStructure.ctc / 12) <= 21000 ? Math.round((selectedStructure.ctc / 12) * 0.0075) : 0) +
                                                    200 +
                                                    Math.round(selectedStructure.monthly_tds || 0)
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-6 p-5 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-100">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Estimated Monthly</p>
                                                <h5 className="text-lg font-bold leading-tight">Net Take Home</h5>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-3xl font-black">
                                                    {formatIndianCurrency(
                                                        (selectedStructure.ctc / 12)
                                                        - calculatePFEstimate(selectedStructure.basic / 12, pfConfig).employeePF
                                                        - ((selectedStructure.ctc / 12) <= 21000 ? Math.round((selectedStructure.ctc / 12) * 0.0075) : 0)
                                                        - 200
                                                        - Math.round(selectedStructure.monthly_tds || 0)
                                                    )}
                                                </p>
                                                <p className="text-[10px] opacity-70 italic">Calculated per month</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-[10px] text-amber-800 leading-relaxed italic">
                                        * Projections include standard deductions ({formatIndianCurrency(75000)} or {formatIndianCurrency(50000)}) and verified investments ({formatIndianCurrency(selectedStructure.eligible_deductions)}) for tax calculation.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter className="mt-4 border-t pt-4">
                        <Button variant="secondary" onClick={() => setIsDetailsOpen(false)}>Close Overview</Button>
                        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => window.print()}>Print Structure</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isExportOpen} onOpenChange={setIsExportOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
                    <DialogHeader className="p-6 pb-0">
                        <div className="flex items-center justify-between">
                            <div>
                                <DialogTitle className="text-xl">Export Salary Data</DialogTitle>
                                <DialogDescription>
                                    Select the components you want to include in the Excel export for {Object.keys(rowSelection).length} records.
                                </DialogDescription>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectedExportFields(SALARY_EXPORT_FIELDS.map(f => f.id))}
                                >
                                    Select All
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectedExportFields([])}
                                >
                                    Clear All
                                </Button>
                            </div>
                        </div>
                    </DialogHeader>

                    <ScrollArea className="flex-1 p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {['Basic', 'Components', 'Statutory'].map(category => (
                                <div key={category} className="space-y-4">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-bold text-sm text-slate-900 uppercase tracking-wider">{category}</h3>
                                        <Badge variant="secondary" className="text-[10px] font-bold h-4 px-1.5 leading-none">
                                            {SALARY_EXPORT_FIELDS.filter(f => f.category === category).length}
                                        </Badge>
                                    </div>
                                    <div className="space-y-3">
                                        {SALARY_EXPORT_FIELDS.filter(f => f.category === category).map(field => (
                                            <div key={field.id} className="flex items-center space-x-2 group cursor-pointer" onClick={() => {
                                                setSelectedExportFields(prev =>
                                                    prev.includes(field.id) ? prev.filter(id => id !== field.id) : [...prev, field.id]
                                                );
                                            }}>
                                                <Checkbox
                                                    id={`export-${field.id}`}
                                                    checked={selectedExportFields.includes(field.id)}
                                                    onCheckedChange={(checked) => {
                                                        setSelectedExportFields(prev =>
                                                            checked ? [...prev, field.id] : prev.filter(id => id !== field.id)
                                                        );
                                                    }}
                                                />
                                                <label
                                                    htmlFor={`export-${field.id}`}
                                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer group-hover:text-primary transition-colors"
                                                >
                                                    {field.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </ScrollArea>

                    <Separator />

                    <DialogFooter className="p-6 bg-slate-50/50">
                        <div className="flex-1 flex items-center">
                            <p className="text-xs text-muted-foreground font-medium">
                                {selectedExportFields.length} out of {SALARY_EXPORT_FIELDS.length} components selected
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setIsExportOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={performExport} className="min-w-[120px]">
                                <Download className="mr-2 h-4 w-4" />
                                Export Excel
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SalaryStructure;
